import { css, useTheme } from '@emotion/react';
import { ListItemNode, ListNode } from '@lexical/list';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import * as Sentry from '@sentry/react';
import type { EditorState, LexicalEditor } from 'lexical';
import { useCallback, useRef } from 'react';

import { lexicalTheme } from './lexical-theme';
import { SectionDividerNode } from './section-divider-node';
import { SpeakerNoteNode } from './speaker-note-node';
import { SectionTitlePlugin } from './section-title-plugin';
import { editorStateToPlainText, plainTextToEditorState } from './serialization';
import { ToolbarPlugin } from './toolbar-plugin';

import { fontSizes, spacing } from '@/theme/tokens';

export function handleLexicalError(error: Error) {
  Sentry.captureException(error);
}

interface LexicalScriptEditorProps {
  initialContent: string;
  onChange: (value: string) => void;
  onEditorReady?: (editor: LexicalEditor) => void;
  readOnly?: boolean;
  onExport?: () => void;
}

export function LexicalScriptEditor({
  initialContent,
  onChange,
  onEditorReady,
  readOnly = false,
  onExport,
}: LexicalScriptEditorProps) {
  const theme = useTheme();
  const editorRef = useRef<LexicalEditor | null>(null);

  const handleChange = useCallback(
    (editorState: EditorState) => {
      const text = editorStateToPlainText(editorState);
      onChange(text);
    },
    [onChange],
  );

  const initialConfig = {
    namespace: 'SonematicEditor',
    theme: lexicalTheme,
    nodes: [SectionDividerNode, SpeakerNoteNode, ListNode, ListItemNode],
    editorState: () => plainTextToEditorState(initialContent),
    onError: handleLexicalError,
    editable: !readOnly,
  };

  return (
    <div
      css={css`
        flex: 1;
        display: flex;
        flex-direction: column;
        border: 1px solid ${theme.colors.border};
        border-radius: 8px;
        background: ${theme.colors.background};
        overflow: hidden;

        &:focus-within {
          border-color: ${theme.colors.primary};
        }

        .lexical-paragraph {
          margin: 0;
        }

        .lexical-bold {
          font-weight: 700;
        }

        .lexical-italic {
          font-style: italic;
        }

        .lexical-strikethrough {
          text-decoration: line-through;
        }

        .lexical-underline {
          text-decoration: underline;
        }

        .lexical-ol {
          padding-left: ${spacing.lg};
          margin: ${spacing.xs} 0;
          list-style-type: decimal;
        }

        .lexical-ul {
          padding-left: ${spacing.lg};
          margin: ${spacing.xs} 0;
          list-style-type: disc;
        }

        .lexical-listitem {
          margin: ${spacing.xs} 0;
        }

        .lexical-nested-listitem {
          list-style-type: none;
        }
      `}
    >
      <LexicalComposer initialConfig={initialConfig}>
        {!readOnly && <ToolbarPlugin onExport={onExport} />}
        <EditorRefPlugin onEditorReady={onEditorReady} editorRef={editorRef} />
        <div
          css={css`
            flex: 1;
            overflow-y: auto;
            min-height: 200px;
            position: relative;
            background: inherit;
          `}
        >
          <SectionTitlePlugin />
          <RichTextPlugin
            contentEditable={
              <ContentEditable
                aria-label="Script editor"
                css={css`
                  padding: ${spacing.md};
                  color: ${theme.colors.text};
                  font-family: inherit;
                  font-size: ${fontSizes.md};
                  line-height: 1.6;
                  outline: none;
                `}
              />
            }
            placeholder={
              <div
                css={css`
                  position: absolute;
                  top: ${spacing.md};
                  left: ${spacing.md};
                  color: ${theme.colors.textSecondary};
                  font-size: ${fontSizes.md};
                  pointer-events: none;
                  user-select: none;
                `}
              >
                Start writing your script here...
              </div>
            }
            ErrorBoundary={LexicalErrorBoundary}
          />
        </div>
        <ListPlugin />
        <HistoryPlugin />
        <OnChangePlugin onChange={handleChange} ignoreSelectionChange />
      </LexicalComposer>
    </div>
  );
}

function EditorRefPlugin({
  onEditorReady,
  editorRef,
}: {
  onEditorReady?: (editor: LexicalEditor) => void;
  editorRef: React.MutableRefObject<LexicalEditor | null>;
}) {
  const [editor] = useLexicalComposerContext();

  if (editorRef.current !== editor) {
    editorRef.current = editor;
    onEditorReady?.(editor);
  }

  return null;
}
